import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Typography, Link as MuiLink } from '@mui/material';
import useApiManager from '../../../../utils/Api/apiManager';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ProposalStatus } from '../../../../utils/enums';

const ProposalsDialog = ({ open, onClose, contactId }) => {
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(false);
    const { get } = useApiManager();

    useEffect(() => {
        if (open && contactId) {
            fetchProposals();
        }
    }, [open, contactId]);

    const fetchProposals = async () => {
        setLoading(true);
        try {
            const data = await get(`/proposals/by-contact/${contactId}`);
            setProposals(data);
        } catch (error) {
            console.error('Error fetching proposals:', error);
        } finally {
            setLoading(false);
        }
    };

    const getProposalStatusLabel = (statusValue) => {
        const status = ProposalStatus.find((s) => s.value === statusValue);
        return status ? status.label : 'Unknown';
    };

    return (
        <Dialog
            fullWidth
            maxWidth={'md'}
            open={open}
            onClose={onClose}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Proposals</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : proposals.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <Typography color="textSecondary">No proposals found</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Topic</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Deadline</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {proposals.map((proposal) => (
                                    <TableRow
                                        key={proposal.id}
                                        hover
                                    >
                                        <TableCell>
                                            <Link 
                                                to={`/proposals/${proposal.id}`}
                                                style={{ textDecoration: 'none' }}
                                            >
                                                <MuiLink
                                                    component="span"
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {proposal.topic}
                                                </MuiLink>
                                            </Link>
                                        </TableCell>
                                        <TableCell>{getProposalStatusLabel(proposal.proposalStatus)}</TableCell>
                                        <TableCell>{proposal.amount ? `$${proposal.amount}` : '-'}</TableCell>
                                        <TableCell>
                                            {proposal.date ? format(new Date(proposal.date), 'MMM dd, yyyy') : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {proposal.deadline ? format(new Date(proposal.deadline), 'MMM dd, yyyy') : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ProposalsDialog;

